import {useCallback} from 'react';

import {UseFilterNameResolver} from 'filters/features/types';
import {PolicyPageFilter} from 'filters/types';
import {
  DatasourceType,
  FilterRange,
  FilterRangeMode,
  InclusionDateType,
  Period,
  PeriodType,
  PolicyAmountType,
  PolicyStatusType,
  UserIdAndSourceCodes,
} from 'modules/decisionsNext/api/types';

import {createEnumMap} from 'utils/createEnumMap';
import {dateFormatter} from 'utils/dateUtils';
import {pluralize} from 'utils/formatters';

import {brokerMarketStatusesOptions} from 'modules/decisionsNext/components/BaseFilter/constants';
import {amountTypeLabelMap} from 'modules/decisionsNext/components/BaseFilter/utils/period';

const FALLBACK = '<Unknown>';

const datasourceTypeMap = createEnumMap(
  {
    [DatasourceType.ProductionCredit]: 'Production Credit',
    [DatasourceType.AgencyData]: 'Primary Producer',
  },
  DatasourceType,
  FALLBACK
);

const policyStatusTypeMap = createEnumMap(
  {
    [PolicyStatusType.Ams]: 'AMS Policy Status',
    [PolicyStatusType.Ennabl]: 'ennabl Policy Status',
  },
  PolicyStatusType,
  FALLBACK
);

export const usePolicyFilterNameResolver: UseFilterNameResolver<PolicyPageFilter> = () => {
  return {
    resolverLoading: false,
    resolveName: useCallback(filter => {
      return [
        {
          field: 'period',
          getValue: () => buildPeriodValue(filter.period, filter.additionalPeriods),
        },
        {
          field: 'datasourceType',
          getValue: () => filter.datasourceType && datasourceTypeMap[filter.datasourceType],
        },
        {
          field: 'policyStatusType',
          getValue: () => filter.policyStatusType && policyStatusTypeMap[filter.policyStatusType],
        },
        {
          field: 'policyStatuses',
          getValue: () => {
            const value = filter.policyStatusesAnd?.[0] ? filter.policyStatusesAnd[0]?.statuses : filter.policyStatuses;

            return buildTreeEntityValue(value ?? [], {singular: 'policy status', plural: 'policy statuses'});
          },
        },
        {
          field: 'direct',
          getValue: () => filter.direct !== undefined && (filter.direct ? 'Direct' : 'Indirect'),
        },
        {
          field: 'brokerMarketStatuses',
          getValue: () =>
            filter.brokerMarketStatuses
              ? brokerMarketStatusesOptions
                  .filter(option => filter.brokerMarketStatuses?.includes(option.value))
                  .map(option => option.label)
                  .join(', ')
              : undefined,
        },
        {
          field: 'carrierIds',
          getValue: () =>
            buildTreeEntityValue(
              [filter.ultimateCarrierIds, filter.parentCarrierIds, filter.issuingPaperIds].filter(Boolean).flat(),
              {singular: 'carrier', plural: 'carriers'}
            ),
        },
        {
          field: 'intermediaryIds',
          getValue: () =>
            buildTreeEntityValue(
              [filter.ultimateIntermediaryIds, filter.parentIntermediaryIds, filter.childIntermediaryIds]
                .filter(Boolean)
                .flat(),
              {singular: 'intermediary', plural: 'intermediaries'}
            ),
        },
        {
          field: 'productFilters',
          getValue: () =>
            buildTreeEntityValue(
              filter.productFilters?.[0]
                ? Object.values(filter.productFilters[0]).filter(Boolean).flat()
                : [
                    filter.productInsuranceSegmentIds,
                    filter.productSuperGroupIds,
                    filter.productGroupIds,
                    filter.productIds,
                  ]
                    .filter(Boolean)
                    .flat(),
              {singular: 'product', plural: 'products'}
            ),
        },
        {
          field: 'departmentLevel',
          getValue: () =>
            buildTreeEntityValue(filter.departmentLevel ? [filter.departmentLevel] : [], {
              singular: 'department level',
              plural: 'department levels',
            }),
        },
        {
          field: 'departmentIds',
          getValue: () => buildTreeEntityValue(filter.departmentIds ?? [], {singular: 'biz org', plural: 'biz orgs'}),
        },
        {
          field: 'industryFilters',
          getValue: () =>
            buildIndustryValue(
              filter.industryFilters?.[0]
                ? Object.values(filter.industryFilters[0]).filter(Boolean).flat()
                : [filter.industryGroupIds, filter.industrySubGroupIds, filter.industryIds].filter(Boolean).flat(),
              filter.naicsSourceConfidenceScore
            ),
        },
        {
          field: 'userFilters',
          getValue: () =>
            buildUserFiltersValue(filter.userFiltersAnd ? filter.userFiltersAnd.at(0) : filter.userFilters),
        },
        {
          field: 'location',
          getValue: () =>
            buildTreeEntityValue(filter.location?.states ?? [], {
              singular: 'account location',
              plural: 'account locations',
            }),
        },
        {
          field: 'clientAgencyCodes',
          getValue: () =>
            buildTreeEntityValue(filter.clientAgencyCodes ?? [], {singular: 'client tag', plural: 'client tags'}),
        },
        {
          field: 'policySources',
          getValue: () => buildPolicySourcesValue(filter.policySourcesFilter),
        },
        {
          field: 'policyAgencyCodes',
          getValue: () =>
            buildTreeEntityValue(filter.policyAgencyCodes ?? [], {singular: 'policy tag', plural: 'policy tags'}),
        },
        {
          field: 'retailAgencyCodes',
          getValue: () =>
            buildTreeEntityValue(filter.retailAgencyCodes ?? [], {singular: 'agency tag', plural: 'agency tags'}),
        },
      ];
    }, []),
  };
};

const inclusionTypeMap = createEnumMap(
  {[InclusionDateType.Inception]: 'eff', [InclusionDateType.Transaction]: 'tr', [InclusionDateType.Expiration]: 'exp'},
  InclusionDateType,
  FALLBACK
);

const _buildDateRange = (period: Period) => {
  const map = {
    [PeriodType.AllTime]: 'All time',
    [PeriodType.FromTo]: `${dateFormatter(period.from)} - ${dateFormatter(period.to)}`,
    [PeriodType.Rolling_12Months]: 'Rolling 12',
    [PeriodType.Ytd]: 'YTD',
    [PeriodType.InForce]: `Inforce ${dateFormatter(period.timestamp)}`,
  };

  if (!period.type || !(period.type in map)) {
    return FALLBACK;
  }

  return map[period.type as PeriodType];
};

const buildPeriodValue = (
  period: PolicyPageFilter['period'],
  additionalPeriods: PolicyPageFilter['additionalPeriods']
): string | undefined => {
  if (!period) {
    return;
  }

  let periodValue = `${amountTypeLabelMap[period.amountType ?? PolicyAmountType.Billed]} ${
    inclusionTypeMap[period.inclusionDateType ?? InclusionDateType.Transaction]
  } ${_buildDateRange(period)}`;

  if (additionalPeriods?.length) {
    periodValue += ` (${additionalPeriods
      .map(p => `${inclusionTypeMap[p.inclusionDateType ?? InclusionDateType.Transaction]} ${_buildDateRange(p)}`)
      .join(', ')})`;
  }

  return periodValue;
};

const buildUserFiltersValue = (userFilters: PolicyPageFilter['userFilters']): string | undefined => {
  if (!userFilters) {
    return;
  }

  const {
    accountExecutiveIdsAndSourceCodes,
    accountExecutiveIsEmpty,
    accountManagerIdsAndSourceCodes,
    accountManagerIsEmpty,
    producerIdsAndSourceCodes,
    producerIsEmpty,
  } = userFilters;

  const formatUserEntity = (
    entities: UserIdAndSourceCodes[] | undefined | null,
    withEmpty: boolean | undefined | null,
    {
      singular,
      plural,
      codeSingular,
      codePlural,
    }: {
      singular: string;
      plural: string;
      codeSingular: string;
      codePlural: string;
    }
  ): string[] => {
    if (!entities?.length) {
      if (withEmpty) {
        return [`Empty ${singular}`];
      }

      return [];
    }

    const users = entities.filter(item => !item.sourceCodes?.length);
    const codes = entities.reduce((acc, item) => acc + (item.sourceCodes?.length || 0), 0);

    const userCount = users.length + (withEmpty ? 1 : 0);

    return [
      userCount ? `${userCount} ${pluralize(userCount, singular, plural)}` : undefined,
      codes ? `${codes} ${pluralize(codes, codeSingular, codePlural)}` : undefined,
    ].filter(Boolean) as string[];
  };

  return [
    ...formatUserEntity(producerIdsAndSourceCodes, producerIsEmpty, {
      singular: 'producer',
      plural: 'producers',
      codeSingular: 'producer code',
      codePlural: 'producer codes',
    }),
    ...formatUserEntity(accountManagerIdsAndSourceCodes, accountManagerIsEmpty, {
      singular: 'account manager',
      plural: 'account managers',
      codeSingular: 'account manager code',
      codePlural: 'account manager codes',
    }),
    ...formatUserEntity(accountExecutiveIdsAndSourceCodes, accountExecutiveIsEmpty, {
      singular: 'account executive',
      plural: 'account executives',
      codeSingular: 'account executive code',
      codePlural: 'account executive codes',
    }),
  ]
    .filter(Boolean)
    .join(', ');
};

export const buildTreeEntityValue = (
  ids: string[],
  {
    singular,
    plural,
  }: {
    singular: string;
    plural: string;
  }
) => {
  if (!ids?.length) {
    return;
  }

  return `${ids.length} ${pluralize(ids.length, singular, plural)}`;
};

const buildFilterRangeValue = (range: FilterRange[] | null | undefined, isPercent: boolean) => {
  if (!range?.length) {
    return;
  }

  const end = isPercent ? '%' : '';

  return range
    .map(r => {
      if (r.notExists) {
        return 'None';
      }

      if (!r.from && r.to) {
        const sign = r.mode === FilterRangeMode.IncludeMax || r.mode === FilterRangeMode.IncludeLimits ? '≤' : '<';

        return `${sign}${r.to}${end}`;
      }

      if (r.from && !r.to) {
        const sign = r.mode === FilterRangeMode.IncludeMin || r.mode === FilterRangeMode.IncludeLimits ? '≥' : '>';

        return `${sign}${r.from}${end}`;
      }

      if (r.from && r.to) {
        return `${r.from}${end} - ${r.to}${end}`;
      }

      return;
    })
    .join(' or ');
};

const buildIndustryValue = (ids: string[] | undefined, confidenceScore: FilterRange[] | null | undefined) => {
  const industriesValue = buildTreeEntityValue(ids ?? [], {
    singular: 'industry',
    plural: 'industries',
  });

  const confidenceScoreValue = buildFilterRangeValue(confidenceScore, true);

  if (!industriesValue && confidenceScoreValue) {
    return `Confidence Score: ${confidenceScoreValue}`;
  }

  return [industriesValue, confidenceScoreValue].filter(Boolean).join(' with confidence score ');
};

const buildPolicySourcesValue = (policySourcesFilter: PolicyPageFilter['policySourcesFilter']): string | undefined => {
  if (!policySourcesFilter) {
    return;
  }

  const {isEmpty, sources} = policySourcesFilter;
  const count = (sources?.length ?? 0) + (isEmpty ? 1 : 0);

  if (count === 0) {
    return;
  }

  return `${count} ${pluralize(count, 'policy source', 'policy sources')}`;
};
